import { Building2, Plus, X } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/lib/auth";
import { resolveDashboardMode } from "@/lib/dashboard-mode";
import { getRoleLabel, papeisAtribuiveis, type InviteRole } from "./role-meta";

export type OrganizacaoDoConvite = { organizationId: string; name: string; types: string[]; role: InviteRole };

/**
 * "Adicionar outra organização" no convite: a mesma pessoa entra em várias organizações de uma
 * vez, cada uma com o seu acesso. Começa como Dentista (Técnico, no laboratório) e se edita aqui.
 * Só aparecem as organizações em que quem convida gerencia a equipe.
 */
export function OutrasOrganizacoesDoConvite({
  valor,
  onChange,
}: {
  valor: OrganizacaoDoConvite[];
  onChange: (v: OrganizacaoDoConvite[]) => void;
}) {
  const { user } = useAuth();
  const ativaId = user?.activeOrganization?.id;
  const convidaveis = trpc.team.invite.organizacoesConvidaveis.useQuery();
  const disponiveis = (convidaveis.data ?? []).filter(
    (o) => o.id !== ativaId && !valor.some((v) => v.organizationId === o.id),
  );

  if ((convidaveis.data ?? []).filter((o) => o.id !== ativaId).length === 0) return null;

  return (
    <div className="space-y-2">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
        Também convidar para
      </p>
      {valor.map((v) => {
        const mode = resolveDashboardMode(v.types as never);
        return (
          <div key={v.organizationId} className="flex items-center gap-2 rounded-lg border border-zinc-200 p-2 dark:border-border">
            <Building2 className="h-4 w-4 shrink-0 text-zinc-400" />
            <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-zinc-800 dark:text-foreground">{v.name}</span>
            <select
              aria-label={`Acesso em ${v.name}`}
              value={v.role}
              onChange={(e) =>
                onChange(valor.map((x) => (x.organizationId === v.organizationId ? { ...x, role: e.target.value as InviteRole } : x)))
              }
              className="h-8 rounded-md border border-zinc-200 bg-white px-2 text-[12.5px] dark:border-border dark:bg-card"
            >
              {papeisAtribuiveis(mode).map((r) => (
                <option key={r} value={r}>
                  {getRoleLabel(r, mode)}
                </option>
              ))}
            </select>
            <button
              type="button"
              aria-label={`Tirar ${v.name}`}
              onClick={() => onChange(valor.filter((x) => x.organizationId !== v.organizationId))}
              className="flex h-8 w-8 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-muted"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
      {disponiveis.length > 0 && (
        <label className="flex items-center gap-2 text-[12.5px] font-semibold text-brand-700 dark:text-primary">
          <Plus className="h-3.5 w-3.5" />
          <select
            aria-label="Adicionar outra organização"
            value=""
            onChange={(e) => {
              const org = disponiveis.find((o) => o.id === e.target.value);
              if (org) onChange([...valor, { organizationId: org.id, name: org.name, types: org.types, role: "DENTIST" }]);
            }}
            className="h-8 cursor-pointer rounded-md border border-dashed border-brand-300 bg-white px-2 text-[12.5px] font-semibold text-brand-700 dark:border-primary/40 dark:bg-card dark:text-primary"
          >
            <option value="">Adicionar outra organização</option>
            {disponiveis.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}
